import { Button, Icon, SegmentedCard, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppLoader } from '@hero/atoms';
import { TagWorksInfoModal } from '@hero/components/modals';
import { AUTOMATION_CONTACTS_TAB, AUTOMATION_CREATION_STEPS } from '@hero/constants';
import { useAutomationCreationHeader } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { OnboardingCard } from '@hero/molecules';
import { ROUTE_PATHS } from '@hero/routes';
import { useUpdateAutomation } from '@hero/services/order-service';
import { useNewAutomationStore, useOnboardingStore, useValidateRules } from '@hero/store/hooks';
import { getRecipientsTypeRule } from '@hero/validations';

import * as S from './style';

function NewAutomationRules() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { code, contacts, updateContacts } = useNewAutomationStore();

  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isTagWorksModalOpen, setIsTagWorksModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(AUTOMATION_CONTACTS_TAB[contacts?.type] || '');
  const [isWithSendCard, setIsWithSendCard] = useState(true);
  const [isWithoutSendCard, setIsWithoutSendCard] = useState(false);

  const header = useAutomationCreationHeader({
    step: 2,
    setIsPageLoading
  });
  const { mutateAsync: updateAutomation, isLoading: isLoadingUpdateAutomation } =
    useUpdateAutomation();

  const onboarding = useOnboardingStore();

  const contactTypeOptions = [
    {
      icon: <Icon name="contacts" size="xxl" />,
      title: <Typography variant="body1SemiBold">{t('selectContacts')}</Typography>,
      value: AUTOMATION_CONTACTS_TAB.contacts,
      description: t('individualSendDesc'),
      bottomContent: (
        <>
          <S.TabDescriptionText as="div" variant="caption1">
            <Icon name="success" />
            {t('contactBasedCardText')}
          </S.TabDescriptionText>

          <OnboardingCard
            styles={{
              wrapper: {
                right: 34,
                top: 17
              }
            }}
            cardOffset={[18, 24]}
            position="right-start"
            isCardShown={isWithSendCard}
            title={t('selectFromContacts')}
            description={t('contactsOnboardingCard2Desc')}
            setIsCardShown={setIsWithSendCard}
            isInfoActive={onboarding?.automation?.infoCardsActive}
            featureType="automation"
            onTourButtonClick={() => {
              setIsWithoutSendCard(true);
            }}
          />
        </>
      )
    },
    {
      icon: <Icon name="tag" size="xxl" />,
      title: <Typography variant="body1SemiBold">{t('selectTags')}</Typography>,
      value: AUTOMATION_CONTACTS_TAB.tags,
      description: t('tagBasedBulkSendDesc'),
      bottomContent: (
        <>
          <S.TabDescriptionText as="div" variant="caption1">
            <Icon name="success" px={16} space={2} />
            <Trans
              i18nKey="tagBasedCardText"
              components={{
                a: (
                  <Button
                    variant="link"
                    color="neutral"
                    onClick={() => setIsTagWorksModalOpen(true)}
                  />
                )
              }}
            />
          </S.TabDescriptionText>

          <OnboardingCard
            styles={{
              wrapper: {
                right: 34,
                top: 17
              }
            }}
            cardOffset={[18, -30]}
            position="right-end"
            isCardShown={isWithoutSendCard}
            title={t('sendWithoutContacts')}
            description={t('sendWithoutContactsOnboardingCardDesc')}
            setIsCardShown={setIsWithoutSendCard}
            onTourButtonClick={() => {}}
            isInfoActive={onboarding?.automation?.infoCardsActive}
            featureType="automation"
          />
        </>
      )
    }
  ];

  const { validate } = useValidateRules();

  const onNextButtonClick = async () => {
    const isValid = await validate([{ rule: getRecipientsTypeRule(), value: activeTab }]);
    if (!isValid) return;

    setIsPageLoading(true);
    updateContacts({
      type: AUTOMATION_CONTACTS_TAB[activeTab]
    });

    try {
      await updateAutomation({
        params: {
          code
        },
        body: {
          contacts,
          meta: {
            creationStep: AUTOMATION_CREATION_STEPS.newAutomationRecipients
          }
        }
      });

      navigate(ROUTE_PATHS.NEW_AUTOMATION_RECIPIENTS);
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    if (!code) {
      navigate(ROUTE_PATHS.MY_AUTOMATION_LIST);
    }
  }, []);

  return (
    <PageLayout
      header={header}
      navigation={{
        rightButton: {
          children: t('next'),
          onClick: onNextButtonClick,
          loading: isLoadingUpdateAutomation,
          icon: true
        },
        leftButton: {
          children: t('back'),
          onClick: () => navigate(ROUTE_PATHS.NEW_AUTOMATION_GIFTS)
        }
      }}
      styles={{
        notification: {
          width: 880
        },
        contentInner: {
          alignItems: 'center'
        }
      }}
    >
      <S.Wrapper activeTab={activeTab}>
        <Typography variant="subtitle4Bold">{t('howWouldYouLikeToIncludeRecipients')}</Typography>
        <S.Content>
          <SegmentedCard
            updateHash={false}
            activeTab={activeTab}
            onChange={setActiveTab}
            options={contactTypeOptions}
            direction="vertical"
          />
        </S.Content>
      </S.Wrapper>
      {isPageLoading && <AppLoader />}
      {isTagWorksModalOpen && (
        <TagWorksInfoModal isOpen={isTagWorksModalOpen} onOpen={setIsTagWorksModalOpen} />
      )}
    </PageLayout>
  );
}

export { NewAutomationRules };
